// Receiving paperwork (delivery receipts, packing slips, BOLs) waiting to be linked to a PO: ONE copy
// of the reads and actions, used by the desktop Receiving page (pages/ReceivingPage.jsx) and the
// phone (mobile/screens/ReceivingScreen.jsx). Actions take a `ui` (features/desktopUi.js on the
// desktop, mobile/ui/phoneUi.js on a phone): pin, alert, confirm.
import config from "../../config.js";
import { apiErrorMessage } from "../../utils/apiError";

const API_BASE = config.API_BASE_URL;
const IMAGES = "https://jobq-images.s3.us-east-2.amazonaws.com";
const noop = () => {};

/** GET /receipt_documents?unlinked=true: paperwork no manager has tied to a PO yet. Throws on failure. */
export async function loadUnlinkedReceipts() {
  const res = await fetch(`${API_BASE}/receipt_documents?unlinked=true`);
  if (!res.ok) throw new Error(await apiErrorMessage(res));
  return res.json();
}

/** Where a receipt document is stored (the public images bucket). */
export function receiptUrl(doc) {
  return `${IMAGES}/${doc.s3_key}`;
}

export function receiptIsPdf(doc) {
  return String((doc && doc.s3_key) || "").toLowerCase().endsWith(".pdf");
}

/**
 * Upload a photo / scan of delivery paperwork (POST /receiving/upload-image, PIN). Resolves
 * {reload: true} once saved (after saying so), {error} with the API's reason (the screen shows it
 * where the upload is), or {cancelled} (no file / no PIN).
 */
export async function uploadReceipt({ file, ui, setBusy = noop, onStart = noop }) {
  if (!file) {
    await ui.alert("No file selected.");
    return { cancelled: true };
  }
  const pin = await ui.pin("Enter your 4-digit PIN:");
  if (!pin) return { cancelled: true };
  onStart(); // the PIN is in: the upload starts (the page clears its last error here)
  setBusy(true);
  try {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("pin", pin);
    const res = await fetch(`${API_BASE}/receiving/upload-image`, { method: "POST", body: formData });
    if (!res.ok) throw new Error(await apiErrorMessage(res));
    await res.json();
    await ui.alert("File uploaded successfully.");
    return { reload: true };
  } catch (err) {
    return { error: err.message };
  } finally {
    setBusy(false);
  }
}

/** Delete an unlinked receipt document (asks first). Resolves {reload: true} once deleted. */
export async function deleteReceipt({ docId, ui }) {
  if (!(await ui.confirm("Are you sure you want to delete this document?", { title: "Delete document", confirmLabel: "Delete", danger: true }))) {
    return { reload: false };
  }
  try {
    const res = await fetch(`${API_BASE}/receipt_documents/${docId}`, { method: "DELETE" });
    if (!res.ok) throw new Error(await apiErrorMessage(res));
    await res.json();
    await ui.alert(`Document ${docId} deleted successfully.`);
    return { reload: true };
  } catch (err) {
    await ui.alert("Error deleting document: " + err.message);
    return { reload: false };
  }
}
